import React, { useState } from 'react';
import { Mail, Check, X, Users, AlertCircle, Copy, ExternalLink, Star } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useTeam } from '../../hooks/useTeam';
import { useAuth } from '../../AuthContext';

export const InviteModal = ({ isOpen, onClose, inviteRole = 'member', groupId = null, groupName = '' }) => {
  const [email, setEmail]     = useState('');
  const [status, setStatus]   = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied]   = useState(false);

  const { team, inviteMember } = useTeam();
  const { user } = useAuth();

  if (!isOpen) return null;

  const roleLabel = inviteRole === 'leader' ? 'リーダー' : 'メンバー';
  const roleColor = inviteRole === 'leader' ? '#8b5cf6' : '#4f46e5';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending'); setErrorMsg('');
    try {
      if (!team) throw new Error('チーム情報が見つかりません。先にチームを作成してください。');
      const token = await inviteMember(email, inviteRole, groupId, groupName);
      const functions = getFunctions();
      const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');
      const result = await sendInviteEmail({ email, teamName: team.name, inviteToken: token, inviterName: user.displayName || 'チーム管理者', role: inviteRole });
      if (result.data.method === 'url') {
        setInviteUrl(result.data.inviteUrl); setStatus('url');
      } else {
        setStatus('success');
        setTimeout(() => { handleClose(); }, 2500);
      }
    } catch(error) {
      setStatus('error'); setErrorMsg(error.message || '招待の送信に失敗しました。');
    }
  };

  const handleCopyUrl = () => { navigator.clipboard?.writeText(inviteUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const handleClose = () => { onClose(); setTimeout(() => { setStatus('idle'); setEmail(''); setInviteUrl(''); setCopied(false); setErrorMsg(''); }, 300); };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.6)', backdropFilter: 'blur(4px)' }} onClick={handleClose} />
      <div style={{ position: 'relative', background: '#fff', borderRadius: 16, boxShadow: '0 25px 50px rgba(0,0,0,0.2)', width: '100%', maxWidth: 440 }}>
        <div style={{ padding: '24px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 800, color: '#111827', display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            {inviteRole === 'leader' ? <Star size={20} color={roleColor} /> : <Users size={20} color={roleColor} />}
            {roleLabel}を招待
          </h3>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={20} /></button>
        </div>

        {inviteRole === 'leader' && (
          <div style={{ margin: '0 24px 16px', padding: '10px 14px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10 }}>
            <p style={{ fontSize: 12, color: '#7c3aed', margin: 0 }}>💜 リーダーはメンバー管理・学習ログ閲覧・オーナーへの相談が可能です。</p>
          </div>
        )}

        <div style={{ padding: '0 24px 24px' }}>
          {status === 'success' && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ width: 64, height: 64, background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Check size={32} color="#16a34a" />
              </div>
              <h4 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>招待メールを送信しました！</h4>
              <p style={{ fontSize: 13, color: '#6b7280' }}>{email} 宛に招待リンクを送信しました。</p>
            </div>
          )}

          {status === 'url' && (
            <div>
              <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <p style={{ fontSize: 13, color: '#92400e', marginBottom: 4, fontWeight: 600 }}>📧 メール送信未設定</p>
                <p style={{ fontSize: 12, color: '#78350f' }}>以下のURLをコピーして直接共有してください。</p>
              </div>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginBottom: 16 }}>
                <p style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, fontWeight: 600 }}>招待URL（7日間有効）</p>
                <p style={{ fontSize: 12, color: '#374151', wordBreak: 'break-all', marginBottom: 10 }}>{inviteUrl}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleCopyUrl} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: copied ? '#dcfce7' : roleColor, color: copied ? '#16a34a' : '#fff', border: 'none', borderRadius: 8, padding: '8px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    <Copy size={14} />{copied ? 'コピーしました！' : 'URLをコピー'}
                  </button>
                  <a href={inviteUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontSize: 13, textDecoration: 'none' }}>
                    <ExternalLink size={13} />確認
                  </a>
                </div>
              </div>
              <button onClick={handleClose} style={{ width: '100%', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>閉じる</button>
            </div>
          )}

          {['idle','sending','error'].includes(status) && (
            <form onSubmit={handleSubmit}>
              {status === 'error' && (
                <div style={{ marginBottom: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', display: 'flex', gap: 8 }}>
                  <AlertCircle size={18} color="#ef4444" style={{ flexShrink: 0 }} />
                  <p style={{ fontSize: 13, color: '#dc2626', margin: 0 }}>{errorMsg}</p>
                </div>
              )}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                  招待する{roleLabel}のメールアドレス
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail size={16} color="#9ca3af" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    style={{ width: '100%', paddingLeft: 38, paddingRight: 16, paddingTop: 10, paddingBottom: 10, border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', color: '#111827', boxSizing: 'border-box' }}
                    placeholder="teammate@example.com" disabled={status === 'sending'} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={handleClose} disabled={status === 'sending'}
                  style={{ flex: 1, padding: '10px 0', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#374151', background: '#fff', cursor: 'pointer' }}>
                  キャンセル
                </button>
                <button type="submit" disabled={status === 'sending' || !email}
                  style={{ flex: 1, padding: '10px 0', background: status === 'sending' || !email ? '#c7d2fe' : `linear-gradient(135deg, ${roleColor}, ${roleColor}cc)`, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, color: '#fff', cursor: status === 'sending' || !email ? 'not-allowed' : 'pointer' }}>
                  {status === 'sending' ? '送信中...' : `${roleLabel}を招待`}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
